import { supabase } from "@/lib/supabase";

/**
 * Computes the SHA-256 hex digest of a File using the Web Crypto API.
 */
export async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Uploads a file to Supabase Storage under the given user's folder,
 * deduplicating by SHA-256 hash. Returns a 1-year signed URL.
 *
 * If the same file (same hash) has already been uploaded by this user,
 * the existing URL is returned immediately without re-uploading.
 */
export async function uploadAttachment(
  file: File,
  userId: string
): Promise<string> {
  const hash = await computeFileHash(file);

  // Check for existing upload
  const { data: existing } = await supabase
    .from("chat_attachments")
    .select("url, storage_path")
    .eq("user_id", userId)
    .eq("hash", hash)
    .maybeSingle();

  if (existing?.url) {
    // Legacy records may have a plain (unauthenticated) object URL instead of a
    // signed URL, which will 400 on private buckets. Re-sign those on the fly.
    const isSignedUrl = existing.url.includes("/object/sign/");
    if (isSignedUrl) {
      return existing.url;
    }
    // Regenerate a signed URL using the stored path and update the record.
    const { data: resigned, error: resignError } = await supabase.storage
      .from("chat-attachments")
      .createSignedUrl(existing.storage_path, 60 * 60 * 24 * 365);
    if (!resignError && resigned?.signedUrl) {
      await supabase
        .from("chat_attachments")
        .update({ url: resigned.signedUrl })
        .eq("user_id", userId)
        .eq("hash", hash);
      return resigned.signedUrl;
    }
    // If re-signing fails (file deleted?), fall through to re-upload.
  }

  // Upload to private bucket at {userId}/{hash}{ext}
  const ext = file.name.includes(".")
    ? file.name.slice(file.name.lastIndexOf("."))
    : "";
  const storagePath = `${userId}/${hash}${ext}`;

  const { error: uploadError } = await supabase.storage
    .from("chat-attachments")
    .upload(storagePath, file, { contentType: file.type, upsert: true });

  if (uploadError) {
    throw new Error(`Storage upload failed: ${uploadError.message}`);
  }

  // Generate a long-lived signed URL (1 year)
  const { data: signed, error: signError } = await supabase.storage
    .from("chat-attachments")
    .createSignedUrl(storagePath, 60 * 60 * 24 * 365);

  if (signError || !signed?.signedUrl) {
    throw new Error("Failed to generate signed URL for attachment");
  }

  // Record in DB for future deduplication (upsert in case a legacy row exists)
  const { error: insertError } = await supabase
    .from("chat_attachments")
    .upsert(
      {
        user_id: userId,
        hash,
        storage_path: storagePath,
        filename: file.name,
        media_type: file.type,
        size: file.size,
        url: signed.signedUrl,
      },
      { onConflict: "user_id,hash" }
    );

  if (insertError) {
    throw new Error(`Failed to record attachment: ${insertError.message}`);
  }

  return signed.signedUrl;
}
